/**
 * Friends wall — see what your friends drew today.
 *
 * STA-142 originally landed with hard-coded mock data; STA-150 wired
 * it to the real backend. 2026-05-18 — invite-code flow was merged
 * into the referral code (`todos/merge-invite-and-referral.md`):
 * the user shares their permanent referral code; submitting someone
 * else's code creates a pending friendship the code owner must accept.
 */

import { useEffect, useState } from 'react';
import {
  api,
  formatApiError,
  type FriendshipOut,
  type WallEntry,
  type Verdict,
} from '../api';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';

const VERDICT_TONE: Record<Verdict, { color: string; glow: string }> = {
  super_lucky: { color: '#E0AF68', glow: 'rgba(224,175,104,0.45)' },
  lucky: { color: '#9BD8E8', glow: 'rgba(155,216,232,0.4)' },
  trust: { color: '#C9A4D9', glow: 'rgba(201,164,217,0.4)' },
  caution: { color: '#9690B5', glow: 'rgba(150,144,181,0.35)' },
};

export default function Friends() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [entries, setEntries] = useState<WallEntry[] | null>(null);
  const [pending, setPending] = useState<FriendshipOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [addCode, setAddCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const myCode = user?.referral_code || null;

  const reload = async () => {
    try {
      const [wall, pend] = await Promise.all([
        api.getFriendsWall(),
        api.listPendingFriends(),
      ]);
      setEntries(wall.entries);
      setPending(pend);
    } catch (e) {
      setError(formatApiError(e, '載入失敗，請稍後再試。'));
    }
  };

  useEffect(() => {
    if (!user) return;
    void reload();
  }, [user]);

  const copyMyCode = async () => {
    if (!myCode) return;
    let ok = false;
    try {
      // Async Clipboard API needs a secure context AND a focused document;
      // either is easy to miss (iframes, dev previews, alt-tab) so we
      // always have execCommand as a backstop below.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(myCode);
        ok = true;
      }
    } catch {
      // fall through to execCommand
    }
    if (!ok) {
      const ta = document.createElement('textarea');
      ta.value = myCode;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, myCode.length);
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
      document.body.removeChild(ta);
    }
    if (ok) {
      setCopied(true);
      setError(null);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setError('複製失敗，請手動長按或選取上方碼。');
    }
  };

  const submitFriendCode = async () => {
    const code = addCode.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const r = await api.requestFriendByCode(code);
      setAddCode('');
      if (r.status === 'accepted') {
        setInfo(`已加 ${r.friend_display_name || '朋友'} 為好友！`);
      } else {
        setInfo(
          `已對 ${r.friend_display_name || '對方'} 送出好友申請，等對方確認。`,
        );
      }
      await reload();
    } catch (e) {
      setError(formatApiError(e, '送出失敗，請稍後再試。'));
    } finally {
      setBusy(false);
    }
  };

  const respondToPending = async (id: string, accept: boolean) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (accept) {
        await api.acceptFriendship(id);
        setInfo('已接受好友邀請。');
      } else {
        await api.rejectFriendship(id);
        setInfo('已拒絕好友邀請。');
      }
      await reload();
    } catch (e) {
      setError(formatApiError(e, '處理失敗，請稍後再試。'));
    } finally {
      setBusy(false);
    }
  };

  const toggleReact = async (entry: WallEntry) => {
    if (entry.is_self || entry.i_reacted) return;
    // m9 — apply the optimistic update FIRST so the heart fills
    // instantly, then roll back if the server rejects. The previous
    // version only updated on success which made the like feel sluggish
    // on slow networks, AND the comment lied — there was no rollback,
    // a server failure just silently left the UI un-updated.
    setEntries((prev) =>
      prev
        ? prev.map((e) =>
            e.user_id === entry.user_id
              ? { ...e, i_reacted: true, reactions_count: e.reactions_count + 1 }
              : e,
          )
        : prev,
    );
    try {
      await api.reactToFriend(entry.user_id);
    } catch (e) {
      // Roll back the optimistic update.
      setEntries((prev) =>
        prev
          ? prev.map((row) =>
              row.user_id === entry.user_id
                ? {
                    ...row,
                    i_reacted: false,
                    reactions_count: Math.max(0, row.reactions_count - 1),
                  }
                : row,
            )
          : prev,
      );
      setError(formatApiError(e, '回應失敗，請稍後再試。'));
    }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center text-tarot-muted">
        {t('friends.empty')}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <div className="mp-eyebrow">FRIENDS</div>
        <h1 className="mp-display text-4xl text-tarot-gold mt-3">{t('friends.title')}</h1>
        <p className="text-tarot-muted text-sm mt-3">{t('friends.subtitle')}</p>
      </div>

      {entries === null ? (
        <div className="text-tarot-muted text-center">載入中…</div>
      ) : entries.length === 1 && !entries[0].today ? (
        <div className="card text-center text-tarot-muted">{t('friends.empty')}</div>
      ) : (
        <ul className="space-y-3">
          {entries.map((entry, i) => (
            <li
              key={entry.user_id}
              style={{ ['--list-delay' as string]: `${Math.min(i, 12) * 0.05}s` }}
              className="list-enter"
            >
              <FriendCard
                entry={entry}
                lang={lang}
                t={t}
                onReact={() => toggleReact(entry)}
                meName={user?.display_name || t('friends.you')}
              />
            </li>
          ))}
        </ul>
      )}

      {pending && pending.length > 0 && (
        <div className="mt-10">
          <div className="text-tarot-muted text-xs mb-3">想加你的人</div>
          <ul className="space-y-2">
            {pending.map((req) => (
              <li
                key={req.id}
                className="rounded-xl border border-tarot-gold/15 bg-tarot-card px-4 py-3 flex items-center gap-3"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-none overflow-hidden"
                  style={{ background: 'rgba(245,235,216,0.06)' }}
                  aria-hidden="true"
                >
                  {req.friend.avatar_url ? (
                    <img
                      src={req.friend.avatar_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="mp-display text-tarot-gold">
                      {(req.friend.display_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className="flex-1 text-tarot-text text-sm">
                  {req.friend.display_name || '匿名'}
                </span>
                <button
                  type="button"
                  className="btn-primary text-xs px-3 py-1"
                  disabled={busy}
                  onClick={() => respondToPending(req.id, true)}
                >
                  同意
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs px-3 py-1"
                  disabled={busy}
                  onClick={() => respondToPending(req.id, false)}
                >
                  拒絕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        <div className="card">
          <div className="text-tarot-muted text-xs mb-2">我的碼</div>
          {myCode ? (
            <div className="text-center">
              <code className="px-3 py-2 rounded bg-tarot-purple/20 text-tarot-gold tracking-widest text-lg select-all">
                {myCode}
              </code>
              <button
                type="button"
                onClick={copyMyCode}
                className="btn-ghost w-full mt-3 text-sm"
              >
                {copied ? '已複製 ✓' : '複製'}
              </button>
              <div className="text-tarot-muted text-xs mt-2">
                把這組碼分享給朋友，他們輸入後你會收到好友邀請。
              </div>
            </div>
          ) : (
            <div className="text-tarot-muted text-sm">
              推薦碼暫不可用，請重新整理。
            </div>
          )}
        </div>
        <div className="card">
          <div className="text-tarot-muted text-xs mb-2">輸入朋友的碼</div>
          <div className="flex gap-2">
            <input
              value={addCode}
              onChange={(e) => setAddCode(e.target.value.toUpperCase())}
              placeholder="ABCDEFGH"
              maxLength={16}
              className="flex-1 px-3 py-2 rounded-md bg-tarot-purple/10 border border-tarot-purple/30 text-tarot-text focus:outline-none focus:border-tarot-gold uppercase tracking-widest text-sm"
            />
            <button
              type="button"
              onClick={submitFriendCode}
              disabled={busy || !addCode.trim()}
              className="btn-primary text-sm"
            >
              {busy ? '送出中…' : '送好友邀請'}
            </button>
          </div>
          <div className="text-tarot-muted text-xs mt-2">
            送出後需要對方確認才會變朋友。
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-4 text-center text-sm text-red-400">{error}</p>
      )}
      {info && !error && (
        <p className="mt-4 text-center text-sm text-tarot-gold">{info}</p>
      )}
    </div>
  );
}

function FriendCard({
  entry,
  lang,
  t,
  onReact,
  meName,
}: {
  entry: WallEntry;
  lang: 'en' | 'zh';
  t: (k: any) => string;
  onReact: () => void;
  meName: string;
}) {
  const tone = entry.today ? VERDICT_TONE[entry.today.verdict] : null;
  const initial = (entry.display_name || (entry.is_self ? meName : '?')).slice(0, 1).toUpperCase();
  return (
    <div
      className={`rounded-xl border bg-tarot-card px-5 py-4 flex items-start gap-4 ${
        entry.is_self
          ? 'border-tarot-gold/60 ring-1 ring-tarot-gold/20'
          : 'border-tarot-gold/15'
      }`}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-none overflow-hidden"
        style={{ background: 'rgba(245,235,216,0.06)' }}
        aria-hidden="true"
      >
        {entry.avatar_url ? (
          <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="mp-display text-tarot-gold">{initial}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-tarot-text font-medium">
            {entry.is_self ? meName : entry.display_name || '匿名'}
            {entry.is_self && <span className="ml-1 text-tarot-gold text-xs">★</span>}
          </span>
          <StreakBadge streak={entry.streak} />
        </div>
        {entry.today ? (
          <>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="mp-display text-lg" style={{ color: tone!.color }}>
                {t(`verdict.${entry.today.verdict}` as any)}
              </span>
              <span className="text-tarot-muted text-xs">
                {lang === 'zh' ? entry.today.card_name_zh : entry.today.card_name_en}
              </span>
            </div>
            <p className="mt-2 text-sm italic text-tarot-text/85 mp-display font-normal">
              &ldquo;{entry.today.note}&rdquo;
            </p>
          </>
        ) : (
          <p className="text-tarot-muted text-sm mt-1">{t('friends.no_draw')}</p>
        )}
      </div>
      {!entry.is_self && entry.today && (
        <button
          type="button"
          className={`text-lg flex-none ${
            entry.i_reacted
              ? 'text-tarot-mauve cursor-default'
              : 'text-tarot-muted hover:text-tarot-mauve'
          }`}
          aria-label={t('friends.like')}
          title={t('friends.like')}
          disabled={entry.i_reacted}
          onClick={onReact}
        >
          {entry.i_reacted ? '♥' : '♡'}
          {entry.reactions_count > 0 && (
            <span className="ml-1 text-xs">{entry.reactions_count}</span>
          )}
        </button>
      )}
    </div>
  );
}

function StreakBadge({ streak }: { streak: number }) {
  if (streak < 2) return null;
  let flames = '🔥';
  let color = 'text-orange-400';
  let glow = '';
  if (streak >= 100) {
    flames = '🔥🔥🔥';
    color = 'text-tarot-gold';
    glow = 'drop-shadow-[0_0_8px_rgba(224,175,104,0.7)] animate-pulse';
  } else if (streak >= 30) {
    flames = '🔥🔥🔥';
    color = 'text-red-500';
    glow = 'drop-shadow-[0_0_6px_rgba(239,68,68,0.6)]';
  } else if (streak >= 14) {
    flames = '🔥🔥';
    color = 'text-orange-500';
    glow = 'drop-shadow-[0_0_4px_rgba(249,115,22,0.55)] animate-pulse';
  } else if (streak >= 7) {
    color = 'text-orange-400';
    glow = 'drop-shadow-[0_0_3px_rgba(251,146,60,0.5)]';
  }
  return (
    <span className={`text-xs ${color} ${glow}`}>
      {flames} {streak}
    </span>
  );
}
